import type {
  AuthResponse,
  LoginRequest,
  RegisterRequest,
  CreateInviteResponse,
  MediaUploadResponse,
  Envelope,
} from '@foyer/shared';

/**
 * Thin fetch wrapper. `credentials: 'include'` is required on every auth
 * call so the httpOnly refresh cookie actually travels (ADR-0007) — easy to
 * forget and easy for the failure to look like a mysterious silent logout.
 */

export class ApiError extends Error {
  constructor(
    message: string,
    public status: number,
    public reqId?: string,
    /** The server's error code (e.g. "invalid_credentials") — the client
     * translates by this, falling back to `message` only when unmapped. */
    public code?: string,
  ) {
    super(message);
  }
}

async function request<T>(path: string, init?: RequestInit): Promise<T> {
  const res = await fetch(path, {
    credentials: 'include',
    headers: { 'content-type': 'application/json' },
    ...init,
  });
  const body = await res.json().catch(() => ({}));
  if (!res.ok) {
    throw new ApiError(body.message ?? 'Request failed.', res.status, body.reqId, body.error);
  }
  return body as T;
}

export function login(req: LoginRequest): Promise<AuthResponse> {
  return request('/api/auth/login', { method: 'POST', body: JSON.stringify(req) });
}

export function register(req: RegisterRequest): Promise<AuthResponse> {
  return request('/api/auth/register', { method: 'POST', body: JSON.stringify(req) });
}

/** Silent re-auth on page load, using the httpOnly cookie. null = not logged in. */
export async function refresh(): Promise<AuthResponse | null> {
  try {
    return await request<AuthResponse>('/api/auth/refresh', { method: 'POST' });
  } catch {
    return null;
  }
}

export function logout(accessToken: string): Promise<{ ok: true }> {
  return request('/api/auth/logout', {
    method: 'POST',
    headers: { authorization: `Bearer ${accessToken}` },
  });
}

export function createInvite(accessToken: string): Promise<CreateInviteResponse> {
  return request('/api/invites', {
    method: 'POST',
    headers: { authorization: `Bearer ${accessToken}` },
  });
}

export function listConversations(
  accessToken: string,
): Promise<{ conversations: Array<{ id: string; kind: string; title: string | null }> }> {
  return request('/api/conversations', { headers: { authorization: `Bearer ${accessToken}` } });
}

/** Issue #9: a device with no local history (new account, or one that lost
 * IndexedDB) fetches the most recent page directly from the server. */
export function fetchHistory(
  accessToken: string,
  convId: string,
  before?: number,
): Promise<{ messages: Envelope[] }> {
  const q = before ? `?before=${before}` : '';
  return request(`/api/conversations/${convId}/messages${q}`, {
    headers: { authorization: `Bearer ${accessToken}` },
  });
}

/**
 * Single-shot upload, streamed by the browser from the Blob — not the JSON
 * `request()` helper above, this one is raw binary with metadata in headers.
 */
export async function uploadMedia(
  accessToken: string,
  mediaId: string,
  sha256: string,
  blob: Blob,
  width?: number,
  height?: number,
): Promise<MediaUploadResponse> {
  const res = await fetch('/api/media', {
    method: 'POST',
    credentials: 'include',
    headers: {
      'content-type': blob.type,
      'x-foyer-media-id': mediaId,
      'x-foyer-sha256': sha256,
      ...(width ? { 'x-foyer-width': String(width) } : {}),
      ...(height ? { 'x-foyer-height': String(height) } : {}),
      authorization: `Bearer ${accessToken}`,
    },
    body: blob,
  });
  const body = await res.json().catch(() => ({}));
  if (!res.ok) throw new ApiError(body.message ?? 'Upload failed.', res.status, body.reqId, body.error);
  return body as MediaUploadResponse;
}

export function changePassword(
  accessToken: string,
  currentPassword: string,
  newPassword: string,
): Promise<{ ok: true }> {
  return request('/api/auth/change-password', {
    method: 'POST',
    headers: { authorization: `Bearer ${accessToken}` },
    body: JSON.stringify({ currentPassword, newPassword }),
  });
}

/** Fetches a full-size image as a blob: URL — never a plain <img src> with the
 * token in the URL (ADR-0007: access token stays out of anything logged/stored). */
export async function fetchMediaObjectUrl(accessToken: string, mediaId: string): Promise<string> {
  const res = await fetch(`/api/media/${mediaId}`, {
    credentials: 'include',
    headers: { authorization: `Bearer ${accessToken}` },
  });
  if (!res.ok) throw new ApiError('Could not load image.', res.status);
  const blob = await res.blob();
  return URL.createObjectURL(blob);
}
